import React, { useMemo } from "react";
import { Tag, Typography, Divider } from "antd";
import dayjs from "dayjs";

const { Text, Title } = Typography;

const isPrimitive = (v) =>
  ["string", "number", "boolean"].includes(typeof v) ||
  v === null ||
  v === undefined;

function looksLikeDate(val) {
  if (typeof val !== "string") return false;
  return /^\d{4}-\d{2}-\d{2}/.test(val);
}

function humanizeDate(val) {
  if (!looksLikeDate(val)) return val;
  const d = dayjs(val);
  if (!d.isValid()) return val;
  if (val.length <= 10) return d.format("DD.MM.YYYY");
  return d.format("DD.MM.YYYY HH:mm");
}

function stableStringify(obj) {
  if (!obj) return "—";
  if (isPrimitive(obj)) return String(obj);
  try {
    const seen = new WeakSet();
    const normalize = (value) => {
      if (isPrimitive(value)) return value;
      if (Array.isArray(value)) return value.map(normalize);
      if (typeof value === "object") {
        if (seen.has(value)) return undefined;
        seen.add(value);
        const keys = Object.keys(value).sort((a, b) => a.localeCompare(b));
        const out = {};
        for (const k of keys) out[k] = normalize(value[k]);
        return out;
      }
      return String(value);
    };
    return JSON.stringify(normalize(obj));
  } catch {
    return String(obj);
  }
}

function labelForValue(valueLabels, v) {
  return typeof v === "string" && valueLabels?.[v] ? valueLabels[v] : null;
}

function stringifyValue(v, valueLabels) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Да" : "Нет";

  if (isPrimitive(v)) {
    const valueLabel = labelForValue(valueLabels, v);
    if (valueLabel) return valueLabel;
    if (typeof v === "string" && looksLikeDate(v)) return humanizeDate(v);
    return String(v);
  }
  return stableStringify(v);
}

const defaultIgnorePred = (key) =>
  key === "uuid" ||
  key === "email" ||
  key === "is_notification_sent" ||
  key.endsWith("_uuid");

function makeIgnorePred(ignoreKeys) {
  const rules = (ignoreKeys || []).map((r) =>
    r instanceof RegExp
      ? r
      : new RegExp(`^${r.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`),
  );
  return (key, path) => {
    if (defaultIgnorePred(key)) return true;
    return rules.some((rx) => rx.test(key) || rx.test(path));
  };
}

function sanitize(obj, pred, path = "") {
  if (obj == null || isPrimitive(obj)) return obj;
  if (Array.isArray(obj)) {
    const arr = obj
      .map((v, i) => sanitize(v, pred, `${path}[${i}]`))
      .filter((v) => v !== undefined);
    return arr.length ? arr : undefined;
  }
  if (typeof obj === "object") {
    const out = {};
    Object.keys(obj).forEach((k) => {
      const nextPath = path ? `${path}.${k}` : k;
      if (pred(k, nextPath)) return;
      const sv = sanitize(obj[k], pred, nextPath);
      if (
        sv !== undefined &&
        sv !== null &&
        !(Array.isArray(sv) && sv.length === 0) &&
        sv !== ""
      ) {
        out[k] = sv;
      }
    });
    return Object.keys(out).length ? out : undefined;
  }
  return obj;
}

function labelFor(fieldLabels, key, path) {
  return fieldLabels?.[path] ?? fieldLabels?.[key] ?? key;
}

function sortKeysWithFIOPriority(parentKey, path, keys) {
  const inNameCtxRx =
    /(?:^|\.)(author|executor|employee|coexecutors|co_executors|assignee_acceptor)(?:\.|$|\[\]|$)/i;
  const isName = inNameCtxRx.test(parentKey) || inNameCtxRx.test(path);

  if (!isName) {
    return [...keys].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }

  const lastSet = new Set(["lastname", "last_name", "surname", "family_name"]);
  const firstSet = new Set(["firstname", "first_name", "name", "given_name"]);
  const middleSet = new Set([
    "patronymic",
    "middle_name",
    "middlename",
    "second_name",
  ]);

  const rank = (k) =>
    lastSet.has(k) ? 0 : firstSet.has(k) ? 1 : middleSet.has(k) ? 2 : 100;

  return [...keys].sort((a, b) => {
    const ra = rank(a),
      rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  });
}

export function getChangeFieldRows({
  before,
  after,
  onlyChanged = false,
  ignoreKeys = [],
  fieldLabels = {},
}) {
  const ignorePred = makeIgnorePred(ignoreKeys);
  const beforeClean = sanitize(before, ignorePred) || {};
  const afterClean = sanitize(after, ignorePred) || {};
  const keys = Array.from(
    new Set([
      ...Object.keys(beforeClean || {}),
      ...Object.keys(afterClean || {}),
    ]),
  );

  const items = keys.map((k) => {
    const left = beforeClean?.[k];
    const right = afterClean?.[k];
    const changed = stringifyValue(left) !== stringifyValue(right);
    return {
      key: k,
      displayName: labelFor(fieldLabels, k, k),
      left,
      right,
      changed,
    };
  });

  const filtered = onlyChanged ? items.filter((i) => i.changed) : items;
  filtered.sort((a, b) => {
    if (a.changed !== b.changed) return a.changed ? -1 : 1;
    return a.displayName.localeCompare(b.displayName, undefined, {
      sensitivity: "base",
    });
  });

  return filtered;
}

export default function ChangeFieldView({
  before,
  after,
  changeType,
  onlyChanged = false,
  ignoreKeys = [],
  fieldLabels = {},
  valueLabels = {},
  columnSpan = [4, 10, 10],
  titles,
  formatter,
  className,
  style,
}) {
  const isCreated = changeType === "created";
  const isDeleted = changeType === "deleted";

  const ignorePred = useMemo(() => makeIgnorePred(ignoreKeys), [ignoreKeys]);

  const { beforeClean, afterClean } = useMemo(() => {
    return {
      beforeClean: sanitize(before, ignorePred) || {},
      afterClean: sanitize(after, ignorePred) || {},
    };
  }, [before, after, ignorePred]);

  const rows = useMemo(
    () =>
      getChangeFieldRows({
        before: beforeClean,
        after: afterClean,
        onlyChanged,
        fieldLabels,
      }),
    [beforeClean, afterClean, onlyChanged, fieldLabels],
  );

  const gridTemplate = `${columnSpan[0]}fr ${columnSpan[1]}fr ${columnSpan[2]}fr`;

  const ValueCell = ({ side, k, raw, mate, depth = 0, path = "" }) => {
    const curPath = path || k;

    if (formatter) {
      const v = formatter(k, raw, side);
      return <div>{v}</div>;
    }

    if (isPrimitive(raw)) {
      const changed = stringifyValue(raw) !== stringifyValue(mate);
      const displayValue = stringifyValue(raw, valueLabels);
      const bg = changed
        ? side === "before"
          ? "rgba(255, 0, 0, 0.08)"
          : "rgba(0, 128, 0, 0.08)"
        : "transparent";

      return (
        <div
          style={{
            padding: "9px",
            borderRadius: 6,
            background: bg,
            border: "1px solid rgba(0,0,0,0.06)",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}
          title={displayValue}
        >
          <Text>{displayValue}</Text>
        </div>
      );
    }

    const isArray = Array.isArray(raw);
    const isMateArray = Array.isArray(mate);

    let keysUnion = isArray
      ? Array.from(
          { length: Math.max(raw?.length ?? 0, mate?.length ?? 0) },
          (_, i) => String(i),
        )
      : sortKeysWithFIOPriority(
          k,
          curPath,
          Array.from(
            new Set([
              ...(raw ? Object.keys(raw) : []),
              ...(mate ? Object.keys(mate) : []),
            ]),
          ),
        );

    if (isArray && isMateArray) {
      const changedKeys = keysUnion.filter((childKey) => {
        const idx = Number(childKey);
        const childL = raw?.[idx];
        const childR = mate?.[idx];
        return stringifyValue(childL) !== stringifyValue(childR);
      });

      if (changedKeys.length > 0) {
        keysUnion = changedKeys;
      }
    }

    return (
      <div
        style={{
          padding: "9px",
          borderRadius: 6,
          border: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <ul
          style={{
            margin: 0,
            paddingLeft: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: "5px",
          }}
        >
          {keysUnion.map((childKey) => {
            const nextPath = isArray
              ? `${curPath}[]`
              : `${curPath}.${childKey}`;
            if (!isArray && ignorePred(childKey, nextPath)) return null;

            const childL = isArray ? raw?.[Number(childKey)] : raw?.[childKey];
            const childR = isArray
              ? mate?.[Number(childKey)]
              : mate?.[childKey];

            const childIsPrimitive = isPrimitive(childL);
            const mateIsPrimitive = isPrimitive(childR);
            const bothPrimitive = childIsPrimitive && mateIsPrimitive;
            const leafChanged =
              stringifyValue(childL) !== stringifyValue(childR);
            const displayValue = stringifyValue(childL, valueLabels);

            const label = isArray
              ? null
              : labelFor(fieldLabels, childKey, nextPath);

            return (
              <li key={childKey}>
                {label && (
                  <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 3 }}>
                    {label}
                  </div>
                )}

                {bothPrimitive ? (
                  <div
                    style={{
                      padding: "4px 6px",
                      borderRadius: 4,
                      background: leafChanged
                        ? side === "before"
                          ? "rgba(255, 0, 0, 0.08)"
                          : "rgba(0, 128, 0, 0.08)"
                        : "transparent",
                      border: "1px solid rgba(0,0,0,0.06)",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                      marginLeft: depth ? 4 : 0,
                    }}
                    title={displayValue}
                  >
                    <Text>{displayValue}</Text>
                  </div>
                ) : (
                  <ValueCell
                    side={side}
                    k={`${k}.${childKey}`}
                    raw={childL}
                    mate={childR}
                    depth={depth + 1}
                    path={nextPath}
                  />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div className={className} style={style}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: gridTemplate,
          alignItems: "end",
          gap: 16,
        }}
      >
        <div>
          <Title level={5} style={{ marginTop: 0 }}>
            {titles?.name ?? "Поле"}
          </Title>
        </div>

        <div>
          {before ? (
            <Title level={5} style={{ marginTop: 0 }}>
              {titles?.left ?? "До"}
            </Title>
          ) : !isDeleted ? (
            <Tag color="green">Новый</Tag>
          ) : null}
        </div>

        <div>
          {after ? (
            <Title level={5} style={{ marginTop: 0 }}>
              {titles?.right ?? "После"}
            </Title>
          ) : !isCreated ? (
            <Tag color="red">Удалено</Tag>
          ) : null}
        </div>
      </div>

      <Divider style={{ margin: "12px 0" }} />

      <div style={{ display: "grid", gridAutoRows: "auto", rowGap: 8 }}>
        {rows.map((r) => (
          <div
            key={r.key}
            style={{
              display: "grid",
              gridTemplateColumns: gridTemplate,
              gap: 16,
              alignItems: "stretch",
            }}
          >
            <div
              style={{
                padding: "9px",
                borderRadius: 6,
                border: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                minHeight: 40,
              }}
              title={r.key}
            >
              <Text strong>{r.displayName}</Text>
            </div>

            <ValueCell side="before" k={r.key} raw={r.left} mate={r.right} />
            <ValueCell side="after" k={r.key} raw={r.right} mate={r.left} />
          </div>
        ))}
      </div>
    </div>
  );
}
